/**
 * Table columns for the money owed and paid capabilities, declared once. The Assistant tool builds its fields, rows
 * and model rows from these; the presentation manifests derive their allowed field lists from them.
 */
export type FinancialColumnShape = "documents" | "payments" | "aging" | "summary";
export type FinancialColumnType = "text" | "number" | "boolean" | "calendarDay" | "monthBucket" | "money";

export interface FinancialColumn {
  key: string;
  /** "{counterparty}", "{documents}" and "{adjustment}" take the side's own nouns. */
  label: string;
  /** Path to the value in a governed result row; money paths hold exact cent strings. */
  source: readonly string[];
  type: FinancialColumnType;
  /** Identity columns name the row; the record-naming ones also carry the record's link. */
  identity?: true;
  /** Columns a single side reports; omitted means both sides. */
  side?: "receivables";
}

const counterparty: FinancialColumn[] = [
  { key: "counterpartyName", label: "{counterparty}", source: ["counterpartyName"], type: "text", identity: true },
  { key: "counterpartyStatus", label: "Status", source: ["counterpartyStatus"], type: "text" },
  { key: "transactionCurrencyCode", label: "Currency", source: ["transactionCurrencyCode"], type: "text", identity: true },
];
const behavior = (field: string) => ["paymentBehavior", field];

export const FINANCIAL_COLUMNS: Record<FinancialColumnShape, FinancialColumn[]> = {
  documents: [
    { key: "documentNumber", label: "Document", source: ["number"], type: "text", identity: true },
    ...counterparty,
    { key: "status", label: "Document Status", source: ["status"], type: "text" },
    { key: "totalAmount", label: "Total", source: ["totalAmountCents"], type: "money" },
    { key: "paidAmount", label: "Settled", source: ["paidAmountCents"], type: "money" },
    { key: "openAmount", label: "Open", source: ["openAmountCents"], type: "money" },
    { key: "documentDate", label: "Document Date", source: ["documentDate"], type: "calendarDay" },
    { key: "dueDate", label: "Due", source: ["dueDate"], type: "calendarDay" },
    { key: "daysPastDue", label: "Days Past Due (negative: until due)", source: ["daysPastDue"], type: "number" },
    { key: "openingBalance", label: "Carried Over at Go-live", source: ["openingBalance"], type: "boolean" },
  ],
  payments: [
    { key: "paymentNumber", label: "Payment", source: ["paymentNo"], type: "text", identity: true },
    ...counterparty,
    { key: "cashAmount", label: "Cash", source: ["amountCents"], type: "money" },
    { key: "paidDate", label: "Paid", source: ["paidAt"], type: "calendarDay" },
    { key: "preGoLive", label: "Before Go-live", source: ["preGoLive"], type: "boolean" },
  ],
  aging: [
    ...counterparty,
    { key: "openAmount", label: "Open", source: ["openDocumentCents"], type: "money" },
    { key: "pastDueAmount", label: "Past Due", source: ["pastDueDocumentCents"], type: "money" },
    { key: "creditAmount", label: "Credits", source: ["openCreditCents"], type: "money" },
    { key: "netAmount", label: "Net", source: ["netCents"], type: "money" },
    { key: "currentAmount", label: "Current", source: ["currentCents"], type: "money" },
    { key: "days1To30Amount", label: "1–30 Days", source: ["days1To30Cents"], type: "money" },
    { key: "days31To60Amount", label: "31–60 Days", source: ["days31To60Cents"], type: "money" },
    { key: "days61To90Amount", label: "61–90 Days", source: ["days61To90Cents"], type: "money" },
    { key: "days90PlusAmount", label: "Over 90 Days", source: ["days90PlusCents"], type: "money" },
    { key: "over365Amount", label: "Over 365 Days", source: ["over365Cents"], type: "money" },
    { key: "openingAmount", label: "Carried Over at Go-live", source: ["openingAmountCents"], type: "money" },
    { key: "openDocumentCount", label: "Open {documents}", source: ["openDocumentCount"], type: "number" },
    { key: "oldestDueDate", label: "Oldest Due", source: ["oldestDueDate"], type: "calendarDay" },
    { key: "dueByAmount", label: "Due Through", source: ["dueByCents"], type: "money" },
    { key: "lastPaymentDate", label: "Last Cash Payment", source: behavior("lastPaymentDate"), type: "calendarDay" },
    { key: "usualDaysLate", label: "Median Days Late", source: behavior("usualDaysLate"), type: "number" },
    { key: "recentDaysLate", label: "Recent Median Days Late (90 Days)", source: behavior("recentDaysLate"), type: "number" },
    { key: "nativePaidDocumentCount", label: "{documents} Paid in Cash", source: behavior("nativePaidDocumentCount"), type: "number" },
    { key: "settledOtherwiseCount", label: "Settled Otherwise", source: behavior("settledOtherwiseCount"), type: "number" },
    {
      key: "olderDueCreditsInWindowAmount",
      label: "Older {documents} Credited in Last 90 Days",
      source: behavior("olderDueCreditInWindowCents"),
      type: "money",
    },
    {
      key: "olderDueAdjustmentsInWindowAmount",
      label: "Older {documents} {adjustment} in Last 90 Days",
      source: behavior("olderDueAdjustmentInWindowCents"),
      type: "money",
    },
    { key: "averageDaysToPay", label: "Mean Days to Final Cash", source: behavior("averageDaysToPay"), type: "number" },
    { key: "olderDueOpenAmount", label: "Older {documents} Still Open", source: behavior("olderDueOpenCents"), type: "money" },
    {
      key: "olderDuePaidInWindowAmount",
      label: "Older {documents} Paid in Last 90 Days",
      source: behavior("olderDuePaidInWindowCents"),
      type: "money",
    },
  ],
  summary: [
    ...counterparty,
    { key: "month", label: "Month", source: ["month"], type: "monthBucket" },
    { key: "cashAmount", label: "Cash", source: ["cashAmountCents"], type: "money" },
    { key: "totalAmount", label: "Billed / Invoiced", source: ["documentAmountCents"], type: "money" },
    { key: "appliedAmount", label: "Cash Applied to Invoices", source: ["appliedAmountCents"], type: "money", side: "receivables" },
    {
      key: "appliedCashNote",
      label: "Applied Cash Coverage",
      source: ["appliedAmountUnavailableReason"],
      type: "text",
      side: "receivables",
    },
  ],
};

/** The visible field keys one capability's tables may show. */
export const financialColumnKeys = (shape: FinancialColumnShape, side: "receivables" | "payables"): string[] =>
  FINANCIAL_COLUMNS[shape].filter(column => column.side === undefined || column.side === side).map(column => column.key);
